import type { IconName } from '@travelnest/hui';

/**
 * A property amenity as the guest sees it: the slug the platform sends, the
 * label we show, and the HUI icon that goes with it.
 */
export interface IAmenityDisplay {
	key: string;
	label: string;
	icon: IconName;
}

type AmenityConfig = Omit<IAmenityDisplay, 'key'>;

/**
 * Amenity slugs come from the property content service. Anything not listed
 * here is not shown, so a new amenity needs an entry (and an icon in HUI).
 */
const AMENITIES: Record<string, AmenityConfig> = {
	highchair: { label: 'Highchair', icon: 'Highchair' },
	'coffee maker': { label: 'Coffee maker', icon: 'CoffeeMachine' },
	'wheelchair access': { label: 'Wheelchair access', icon: 'Disability' },
	fireplace: { label: 'Fireplace', icon: 'Fire' },
	'garden furniture': { label: 'Garden furniture', icon: 'PicnicBench' },
	'hot tub': { label: 'Hot tub', icon: 'HotTub' },
	'swimming pool': { label: 'Swimming pool', icon: 'SwimmingPool' },
	balcony: { label: 'Balcony', icon: 'Balcony' },
	garden: { label: 'Garden', icon: 'Flowers' },
	toiletries: { label: 'Toiletries', icon: 'SoapPump' },
	'hair dryer': { label: 'Hairdryer', icon: 'Hairdryer' },
	iron: { label: 'Iron', icon: 'Iron' },
	'carbon monoxide detector': {
		label: 'Carbon monoxide detector',
		icon: 'CoDetector',
	},
	'smoke detector': { label: 'Smoke detector', icon: 'Detector' },
	'private entrance': { label: 'Private entrance', icon: 'Door' },
	'washing machine': { label: 'Washing machine', icon: 'WashingMachine' },
	'tumble dryer': { label: 'Tumble dryer', icon: 'TumbleDryer' },
	linen: { label: 'Linen', icon: 'DoubleBed' },
	towels: { label: 'Towels', icon: 'Wallpaper' },
	view_garden: { label: 'Garden view', icon: 'Flowers' },
	'travel cot': { label: 'Travel cot', icon: 'Cot' },
	BBQ: { label: 'BBQ', icon: 'Bbq' },
	patio: { label: 'Patio', icon: 'PicnicBench' },
	parking: { label: 'Parking', icon: 'Parking' },
	'music player': { label: 'Music player', icon: 'Radio' },
	television: { label: 'Television', icon: 'Monitor' },
	internet: { label: 'WiFi', icon: 'Wifi' },
	heating: { label: 'Heating', icon: 'Gauge' },
	dishwasher: { label: 'Dishwasher', icon: 'Dishwasher' },
	toaster: { label: 'Toaster', icon: 'Bread' },
	kettle: { label: 'Kettle', icon: 'Kettle2' },
	freezer: { label: 'Freezer', icon: 'Ice' },
	fridge: { label: 'Fridge', icon: 'MiniFridge' },
	microwave: { label: 'Microwave', icon: 'Microwave' },
	hob: { label: 'Hob', icon: 'Pot' },
	oven: { label: 'Oven', icon: 'Oven' },
	'air conditioning': { label: 'Air conditioning', icon: 'Fan' },
	view_valley: { label: 'Valley view', icon: 'Trees' },
	view_beach: { label: 'Beach view', icon: 'Beach2' },
	view_sea: { label: 'Sea view', icon: 'Sunrise2' },
	view_lake: { label: 'Lake view', icon: 'Boat' },
	view_mountain: { label: 'Mountain view', icon: 'Mountain' },
	view_city: { label: 'City view', icon: 'Buildings' },
};

/** The amenities we can display, de-duplicated and in the order they were given. */
export const mapAmenities = (amenitySlugs: string[]): IAmenityDisplay[] =>
	Array.from(new Set(amenitySlugs)).flatMap((key) => {
		const amenity = AMENITIES[key];
		return amenity ? [{ key, ...amenity }] : [];
	});
